import { useCallback, useEffect, useState } from 'react';
import { useFilterContext } from '@/context';
import type { DateRange, ShowFilter } from '@/types';

/**
 * This hook is used to manage the data state of a modal.
 * @param isModalOpen - The state of the modal.
 * @returns The data state of the modal and the functions to handle the data state change.
 */
const useModalDataState = (isModalOpen: boolean) => {
  const { filters } = useFilterContext();
  const { dateRange, selectedShows: selectedShowIds } = filters;
  const { from, to } = dateRange;
  const selectedShow: ShowFilter = selectedShowIds[0] ?? 'all';
  const [modalSelectedShow, setModalSelectedShow] = useState<ShowFilter>(selectedShow);
  const [modalDateRange, setModalDateRange] = useState<DateRange>({
    from,
    to,
  });

  const handleSelectedShowChange = useCallback((selectedShow: ShowFilter) => {
    setModalSelectedShow(selectedShow);
  }, []);

  const handleDateRangeChange = useCallback(({ from, to }: DateRange) => {
    setModalDateRange({ from, to });
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      setModalSelectedShow(selectedShow);
    }
  }, [selectedShow, isModalOpen]);

  useEffect(() => {
    if (isModalOpen) {
      setModalDateRange((prevDateRange) => {
        if (prevDateRange.from === from && prevDateRange.to === to) {
          return prevDateRange;
        }
        return {
          from,
          to,
        };
      });
    }
  }, [from, to, isModalOpen]);

  return { modalDateRange, handleDateRangeChange, modalSelectedShow, handleSelectedShowChange };
};

export default useModalDataState;
